import React, { useCallback, useEffect, useRef, useState } from 'react';
import Layout from '@/components/Layout';
import { useParams, useNavigate } from 'react-router-dom';
import { MessageCircle, Send, ChevronLeft, ChevronRight, Search, Users, Loader2, Play, ImagePlus, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const mediaUrl = (p) => (p && p.startsWith('/') ? `${BACKEND_URL}${p}` : p);

function Avatar({ name, src, size = 44 }) {
  const s = { width: size, height: size };
  if (src) return <img src={mediaUrl(src)} alt={name} className="rounded-full object-cover flex-shrink-0" style={s} />;
  return (
    <div className="rounded-full bg-[#8EAA97] flex items-center justify-center text-white font-semibold flex-shrink-0" style={s}>
      {(name?.[0] || '?').toUpperCase()}
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return 'now';
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`;
  return `${Math.floor(d / 86400)}d`;
}

function PhotoGrid({ urls, onOpen }) {
  const list = urls.filter(Boolean);
  if (list.length === 0) return null;
  if (list.length === 1) {
    return <img src={mediaUrl(list[0])} alt="attachment" onClick={() => onOpen(list, 0)} className="rounded-xl mb-1 max-h-56 object-cover cursor-pointer" />;
  }
  const cols = list.length === 2 || list.length === 4 ? 'grid-cols-2' : 'grid-cols-3';
  return (
    <div className={`grid ${cols} gap-1 mb-1 w-[240px]`} data-testid="dm-photo-grid">
      {list.slice(0, 6).map((u, i) => (
        <img key={u} src={mediaUrl(u)} alt="attachment" onClick={() => onOpen(list, i)}
          className="w-full h-[76px] rounded-lg object-cover cursor-pointer" />
      ))}
    </div>
  );
}

export default function Messages({ user }) {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [thread, setThread] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef(null);
  const [showNew, setShowNew] = useState(false);
  const [following, setFollowing] = useState([]);
  const [q, setQ] = useState('');
  const scrollRef = useRef(null);
  const lastCursorRef = useRef(null);

  const loadConversations = useCallback(() => {
    axios.get(`${API}/messages/conversations`, { withCredentials: true })
      .then((r) => setConversations(r.data.conversations || []))
      .catch(() => setConversations([]))
      .finally(() => setLoadingList(false));
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  const loadThread = useCallback((uid, silent) => {
    if (!silent) { setLoadingThread(true); lastCursorRef.current = null; }
    const cursor = silent ? lastCursorRef.current : null;
    const url = `${API}/messages/with/${uid}${cursor ? `?after=${encodeURIComponent(cursor)}` : ''}`;
    axios.get(url, { withCredentials: true })
      .then((r) => {
        if (cursor) {
          // Poll: append only the new messages and refresh read receipts — no full re-fetch.
          setThread((prev) => {
            if (!prev) return r.data;
            const incoming = r.data.messages || [];
            const merged = incoming.length ? [...prev.messages, ...incoming] : prev.messages;
            const msgs = r.data.viewer_all_seen ? merged.map((m) => (m.is_mine ? { ...m, seen: true } : m)) : merged;
            return { ...prev, messages: msgs, viewer_all_seen: r.data.viewer_all_seen };
          });
          const inc = r.data.messages || [];
          if (inc.length) lastCursorRef.current = inc[inc.length - 1].created_at;
        } else {
          setThread(r.data);
          const msgs = r.data.messages || [];
          lastCursorRef.current = msgs.length ? msgs[msgs.length - 1].created_at : null;
        }
      })
      .catch(() => { if (!cursor) setThread(null); })
      .finally(() => setLoadingThread(false));
  }, []);

  useEffect(() => {
    if (!userId) { setThread(null); return; }
    loadThread(userId);
    const t = setInterval(() => loadThread(userId, true), 6000);
    return () => clearInterval(t);
  }, [userId, loadThread]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread?.messages?.length]);

  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(null);
      if (e.key === 'ArrowRight') setLightbox((l) => l && ({ ...l, index: (l.index + 1) % l.urls.length }));
      if (e.key === 'ArrowLeft') setLightbox((l) => l && ({ ...l, index: (l.index - 1 + l.urls.length) % l.urls.length }));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  const openNew = () => {
    setShowNew(true);
    axios.get(`${API}/messages/following`, { withCredentials: true })
      .then((r) => setFollowing(r.data.growers || r.data.following || []))
      .catch(() => setFollowing([]));
  };

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (e.target) e.target.value = '';
    if (!files.length) return;
    setUploading(true);
    try {
      const results = [];
      for (const file of files.slice(0, 6)) {
        const fd = new FormData();
        fd.append('file', file, file.name || 'photo.jpg');
        const r = await axios.post(`${API}/community/upload-photo`, fd, { withCredentials: true });
        if (r.data.photo_url) results.push(r.data.photo_url);
      }
      setPhotos((prev) => [...prev, ...results].slice(0, 6));
    } catch (e2) { /* ignore */ }
    finally { setUploading(false); }
  };
  const removePhoto = (u) => setPhotos((prev) => prev.filter((p) => p !== u));

  const send = async () => {
    const body = text.trim();
    if ((!body && photos.length === 0) || !userId) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/messages/with/${userId}`, { body, photo_urls: photos }, { withCredentials: true });
      setThread((prev) => prev ? { ...prev, messages: [...prev.messages, r.data] } : prev);
      if (r.data?.created_at) lastCursorRef.current = r.data.created_at;
      setText('');
      setPhotos([]);
      loadConversations();
    } catch (e) { /* keep text so user can retry */ }
    finally { setSending(false); }
  };

  const filtered = conversations.filter((c) => !q || (c.display_name || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <Layout user={user}>
      <div className="h-[calc(100vh-160px)] min-h-[520px] flex rounded-3xl overflow-hidden border border-[#1A4331]/10 bg-white/60" data-testid="messages-page">
        {/* Conversation list */}
        <div className={`w-full md:w-[320px] flex-shrink-0 border-r border-[#1A4331]/10 bg-[#FAF6EC] flex-col ${userId ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-4 border-b border-[#1A4331]/10">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>Messages</h2>
              <button onClick={openNew} data-testid="messages-new" className="hg-press text-sm font-semibold text-[#1A4331] bg-white/70 hover:bg-white rounded-full px-3 py-1.5 flex items-center gap-1">
                <Users className="w-4 h-4" /> New
              </button>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8EAA97]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search chats" data-testid="messages-search"
                className="w-full pl-9 pr-3 py-2 bg-white rounded-xl border border-[#1A4331]/10 outline-none text-sm" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto hg-scroll">
            {loadingList ? (
              <div className="p-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#8EAA97]" /></div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#7A6B57]" data-testid="messages-empty">
                <MessageCircle className="w-10 h-10 text-[#8EAA97] mx-auto mb-2" />
                No conversations yet. Tap “New” to message a grower, or reply to a swap request.
              </div>
            ) : filtered.map((c) => (
              <button key={c.conversation_id} onClick={() => navigate(`/messages/${c.other_id}`)} data-testid={`conversation-${c.other_id}`}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/70 transition-colors ${userId === c.other_id ? 'bg-white' : ''}`}>
                <Avatar name={c.display_name} src={c.avatar} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-[#1A4331] truncate">{c.display_name}</span>
                    <span className="text-[11px] text-[#7A6B57] flex-shrink-0">{timeAgo(c.last_at)}</span>
                  </div>
                  <p className="text-sm text-[#5C665C] truncate">{c.last_message || 'Say hello 👋'}</p>
                </div>
                {c.unread > 0 && <span className="min-w-[20px] h-5 px-1 rounded-full bg-[#C66B4A] text-white text-xs font-bold flex items-center justify-center">{c.unread}</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Thread */}
        <div className={`flex-1 flex-col ${userId ? 'flex' : 'hidden md:flex'}`}>
          {!userId ? (
            <div className="flex-1 flex flex-col items-center justify-center text-[#7A6B57]">
              <MessageCircle className="w-14 h-14 text-[#8EAA97] mb-3" />
              <p className="font-semibold text-[#1A4331]">Select a conversation</p>
              <p className="text-sm">Reply to swap requests and chat with fellow growers.</p>
            </div>
          ) : loadingThread ? (
            <div className="flex-1 flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#8EAA97]" /></div>
          ) : (
            <>
              <div className="flex items-center gap-3 px-4 py-3 border-b border-[#1A4331]/10 bg-[#FAF6EC]">
                <button onClick={() => navigate('/messages')} className="md:hidden w-8 h-8 rounded-full hover:bg-white flex items-center justify-center"><ChevronLeft className="w-5 h-5 text-[#1A4331]" /></button>
                <Avatar name={thread?.other?.display_name} src={thread?.other?.avatar} size={38} />
                <span className="font-bold text-[#1A4331]" data-testid="thread-name">{thread?.other?.display_name}</span>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto hg-scroll p-4 space-y-2.5 bg-white/50" data-testid="thread-messages">
                {(thread?.messages || []).map((m, i) => {
                  const last = i === thread.messages.length - 1;
                  return (
                    <div key={m.id} className={`flex ${m.is_mine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[74%] rounded-2xl px-3.5 py-2.5 ${m.is_mine ? 'bg-[#1A4331] text-[#FAF6EC] rounded-br-md' : 'bg-white text-[#2A3528] rounded-bl-md shadow-sm'}`}>
                        {(m.photo_urls?.length || m.photo_url) && (
                          <PhotoGrid urls={m.photo_urls?.length ? m.photo_urls : [m.photo_url]} onOpen={(urls, i) => setLightbox({ urls, index: i })} />
                        )}
                        {m.video_url && <video src={mediaUrl(m.video_url)} controls className="rounded-xl mb-1 max-h-56" />}
                        {m.audio_url && (
                          <span className="inline-flex items-center gap-2 text-sm"><Play className="w-4 h-4" /> Voice note{m.audio_duration ? ` · ${m.audio_duration}s` : ''}</span>
                        )}
                        {m.shared_post && (
                          <div className={`text-xs rounded-xl p-2 mb-1 ${m.is_mine ? 'bg-white/15' : 'bg-[#F0F2EB]'}`}>Shared a post: {m.shared_post.crop_name || 'Harvest'}</div>
                        )}
                        {m.body && <p className="text-[15px] leading-snug whitespace-pre-wrap break-words">{m.body}</p>}
                        <div className={`flex items-center gap-1 mt-0.5 ${m.is_mine ? 'justify-end text-[#B4D330]/70' : 'text-[#8EAA97]'}`}>
                          <span className="text-[10px]">{timeAgo(m.created_at)}</span>
                          {m.is_mine && last && <span className="text-[10px] font-semibold">{m.seen ? '· Seen' : '· Sent'}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {thread?.messages?.length === 0 && <p className="text-center text-sm text-[#7A6B57] mt-8">No messages yet — say hello!</p>}
              </div>

              <div className="p-3 border-t border-[#1A4331]/10 bg-[#FAF6EC]">
                {thread?.can_message === false ? (
                  <p className="text-center text-sm text-[#7A6B57] py-2">You can message this grower once they follow you back or you have an active swap.</p>
                ) : (
                  <div>
                    {photos.length > 0 && (
                      <div className="flex gap-2 mb-2 flex-wrap" data-testid="message-photo-preview">
                        {photos.map((u) => (
                          <div key={u} className="relative">
                            <img src={mediaUrl(u)} alt="attachment" className="h-16 w-16 rounded-xl object-cover" />
                            <button onClick={() => removePhoto(u)} data-testid="message-photo-remove" className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#C66B4A] text-white flex items-center justify-center"><X className="w-3 h-3" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex items-end gap-2">
                      <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={uploadPhotos} data-testid="message-photo-input" />
                      <button onClick={() => fileRef.current?.click()} disabled={uploading || photos.length >= 6} data-testid="message-attach"
                        className="hg-press w-11 h-11 rounded-full bg-white border border-[#1A4331]/10 hover:bg-[#F0F2EB] disabled:opacity-50 flex items-center justify-center flex-shrink-0">
                        {uploading ? <Loader2 className="w-5 h-5 animate-spin text-[#1A4331]" /> : <ImagePlus className="w-5 h-5 text-[#1A4331]" />}
                      </button>
                      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={1} placeholder="Write a message…" data-testid="message-input"
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                        className="flex-1 resize-none max-h-28 px-4 py-2.5 bg-white rounded-2xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331] text-[15px]" />
                      <button onClick={send} disabled={sending || (!text.trim() && photos.length === 0)} data-testid="message-send"
                        className="hg-press w-11 h-11 rounded-full bg-[#1A4331] hover:bg-[#0F2D1F] disabled:opacity-50 text-[#FAF6EC] flex items-center justify-center flex-shrink-0">
                        {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-[80] bg-black/85 flex items-center justify-center" onClick={() => setLightbox(null)} data-testid="message-lightbox">
          <button onClick={() => setLightbox(null)} data-testid="lightbox-close" className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"><X className="w-6 h-6 text-white" /></button>
          {lightbox.urls.length > 1 && (
            <button onClick={(e) => { e.stopPropagation(); setLightbox((l) => ({ ...l, index: (l.index - 1 + l.urls.length) % l.urls.length })); }} data-testid="lightbox-prev" className="absolute left-4 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"><ChevronLeft className="w-6 h-6 text-white" /></button>
          )}
          <img src={mediaUrl(lightbox.urls[lightbox.index])} alt="attachment" onClick={(e) => e.stopPropagation()} className="max-h-[85vh] max-w-[90vw] rounded-2xl object-contain" />
          {lightbox.urls.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); setLightbox((l) => ({ ...l, index: (l.index + 1) % l.urls.length })); }} data-testid="lightbox-next" className="absolute right-4 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"><ChevronRight className="w-6 h-6 text-white" /></button>
              <span className="absolute bottom-5 text-white/80 text-sm font-semibold">{lightbox.index + 1} / {lightbox.urls.length}</span>
            </>
          )}
        </div>
      )}

      {showNew && (
        <div className="fixed inset-0 z-[70] bg-[#0F2D1F]/45 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowNew(false)} data-testid="new-message-modal">
          <div className="bg-[#FAF6EC] rounded-3xl w-full max-w-md max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-[#1A4331] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>New message</h3>
            <p className="text-sm text-[#7A6B57] mb-4">Growers who follow you back.</p>
            {following.length === 0 ? (
              <p className="text-sm text-[#7A6B57] py-6 text-center">No mutual followers yet.</p>
            ) : (
              <div className="space-y-1">
                {following.map((g) => (
                  <button key={g.user_id} onClick={() => { setShowNew(false); navigate(`/messages/${g.user_id}`); }} data-testid={`new-chat-${g.user_id}`}
                    className="w-full flex items-center gap-3 p-2 rounded-2xl hover:bg-white text-left">
                    <Avatar name={g.display_name} src={g.avatar} size={40} />
                    <div><p className="font-semibold text-[#1A4331]">{g.display_name}</p>{g.location && <p className="text-xs text-[#7A6B57]">{g.location}</p>}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
